// AUTO-GENERATED — do not edit. Source: /shared/. Run scripts/sync-shared.sh to update.
/**
 * SLIK — PER-SHOP BOOKING RULES (multi-tenant config brain, Jose 2026-08-07)
 * =========================================================================
 * SLIK is multi-tenant: every barbershop runs its own copy and sets its OWN parameters. Services and
 * their durations are already per-shop (service_categories, tagged by shopId); shop hours are per-shop
 * (publicHoursService). But the BOOKING/TIMING rules were still hardcoded constants scattered across
 * the shared brains. This file makes them ONE per-shop config object that every brain reads, so a shop
 * can tune how booking behaves — back-to-back vs a fixed grid, buffers, new-client length, the no-show
 * policy — from Settings, and the whole app (POS + customer-portal + public-booking) obeys it.
 *
 * Defaults preserve today's behavior. Load a shop's saved rules (Firestore shop doc), run through
 * `mergeBookingRules`, and pass the result into the brains. Pure types + merge — NO React, NO Firebase.
 * Edit THIS file at repo root and run scripts/sync-shared.sh. Do NOT edit per-app copies.
 */

export interface BookingRules {
  /** Offer the exact moment a barber frees up (right after each appointment), not just a fixed grid. */
  backToBack: boolean;
  /** Grid granularity for offered start times (minutes) — the base cadence when not filling gaps. */
  slotIntervalMin: number;
  /** Cleanup / reset minutes AFTER each appointment before the next bookable slot. */
  bufferMin: number;
  /** A no-show still HOLDS its slot (only a cancel frees it). false = a no-show reopens the slot. */
  noShowOccupiesSlot: boolean;

  /** Minimum minutes for a first-timer's appointment (extra time for a new client). */
  newClientMinDuration: number;
  /** Accountability events (no-show OR late-cancel) that trip the no-show block. */
  noShowBlockThreshold: number;
  /** A cancel within this many minutes of the start counts as a no-show strike. */
  lateCancelWindowMin: number;
  /**
   * Does a late cancellation (within lateCancelWindowMin before an UPCOMING appointment) AUTO-count
   * as a no-show strike toward the block? OFF by default (Jose 2026-09-07, opt-in) — Fresha keeps
   * cancel and no-show separate, so a shop enables this only if it wants cancel to auto-penalize.
   * When off, the ONLY way to record a no-show is the deliberate No-show action.
   */
  lateCancelCountsAsNoShow: boolean;
  /** Completed services that could lift a block (only if a shop enables auto-lift). */
  redemptionServices: number;
}

/** Safe defaults — match SLIK's current behavior (back-to-back ON, no-show occupies, 45/2/60/3). */
export const DEFAULT_BOOKING_RULES: BookingRules = {
  backToBack: true,
  slotIntervalMin: 30,
  bufferMin: 0,
  noShowOccupiesSlot: true,
  newClientMinDuration: 45,
  noShowBlockThreshold: 2,
  lateCancelWindowMin: 60,
  lateCancelCountsAsNoShow: false, // opt-in — cancel ≠ no-show unless the shop turns this on
  redemptionServices: 3,
};

/** Merge a shop's (possibly partial / legacy) saved rules over the defaults. Always returns a full set. */
export function mergeBookingRules(partial?: Partial<BookingRules> | null): BookingRules {
  const p = partial || {};
  const num = (v: any, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d);
  const bool = (v: any, d: boolean) => (typeof v === 'boolean' ? v : d);
  return {
    backToBack: bool(p.backToBack, DEFAULT_BOOKING_RULES.backToBack),
    slotIntervalMin: num(p.slotIntervalMin, DEFAULT_BOOKING_RULES.slotIntervalMin),
    bufferMin: num(p.bufferMin, DEFAULT_BOOKING_RULES.bufferMin),
    noShowOccupiesSlot: bool(p.noShowOccupiesSlot, DEFAULT_BOOKING_RULES.noShowOccupiesSlot),
    newClientMinDuration: num(p.newClientMinDuration, DEFAULT_BOOKING_RULES.newClientMinDuration),
    noShowBlockThreshold: num(p.noShowBlockThreshold, DEFAULT_BOOKING_RULES.noShowBlockThreshold),
    lateCancelWindowMin: num(p.lateCancelWindowMin, DEFAULT_BOOKING_RULES.lateCancelWindowMin),
    lateCancelCountsAsNoShow: bool(p.lateCancelCountsAsNoShow, DEFAULT_BOOKING_RULES.lateCancelCountsAsNoShow),
    redemptionServices: num(p.redemptionServices, DEFAULT_BOOKING_RULES.redemptionServices),
  };
}
